// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import { BaseItem, ColumnData } from './BaseItem';
import { ColumnParams } from './ColumnParams';
import { MathUtils } from '@shared/utils/MathUtils';
import { AccountType, getAccountTypeStr } from '@shared/utils/Account/AccountType';
import { QuickTableComparingType } from '@shared/utils/QuickTableMisc/QuickTableComparingType';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { AssetType } from '@shared/commons/cache/Asset';
import { Account } from '@shared/commons/cache/Account';
import { AccountFeature } from '@shared/utils/Account/AccountFeature';
import { DataCache } from '@shared/commons/DataCache';
import { AccountUtils } from '@shared/commons/cache/AccountUtils';
import { AssetBalanceItemColumnsEnum } from '@shared/utils/Enums/AccountDetailsEnum';
import { StringUtils } from '@shared/utils/StringUtils';
import { TableTotalsVisibility } from './TableTotalsVisibility';
import { QTAlignment } from '../elements/QuickTable/QuickTableColumn';
import { ACCOUNT_COLUMN_RULE } from '@shared/utils/Rules/AccountRules';
export class AssetBalanceItem extends BaseItem {
    constructor(account, sessionSettings, assetBalance, EstimatedCurrency) {
        super(sessionSettings);
        this.Account = account;
        this.AssetBalance = null;
        this.EstimatedCurrency = EstimatedCurrency;
        try {
            if (!isNullOrUndefined(assetBalance)) {
                this.AssetBalance = assetBalance;
            }
            else if (!isNullOrUndefined(this.Account)) {
                this.AssetBalance = this.Account.assetBalanceDefault;
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
        }
        if (!isNullOrUndefined(account)) {
            this.itemId = this.Account.AcctNumber + ':' + assetBalance.Asset.Id;
        }
        else {
            this.itemId = 0;
        }
        this.userGroupName = '';
    }
    UseAccountCurrency() {
        return true; /* TODO. DataCache.AccountsAC.UseAccCurr */
    }
    get CrossPrice() {
        return !this.UseAccountCurrency() && !isNullOrUndefined(this.AssetBalance)
            ? DataCache.CrossRateCache.GetCrossPriceExp1Exp2(this.AssetBalance.Asset.Name, DataCache.baseCurrency, this.Account)
            : 1;
    }
    ColumnCount() {
        return AssetBalanceItem.columnsParams.length;
    }
    GetCurrentAccount() {
        return this.Account;
    }
    getColumnData(column) {
        const curAccount = this.Account;
        const AssetBalance = this.AssetBalance;
        const value = this.getColumnValue(column);
        let formattedValue = '';
        switch (column) {
            case AssetBalanceItemColumnsEnum.STATUS_COL_INDEX:
                formattedValue = Resources.getResource('panel.accounts.accountDetails.' + value.toString());
                break;
            case AssetBalanceItemColumnsEnum.BALANCE_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.Balance, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.BLOCKED_BALANCE_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.BlockedBalance, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.PROFIT_NET_COL_INDEX:
                formattedValue =
                    !isNaN(curAccount.ProfitNet) !== null
                        ? AssetBalance.formatPrice(curAccount.ProfitNet)
                        : Resources.getResource('general.Calculating');
                break;
            case AssetBalanceItemColumnsEnum.PROFIT_COL_INDEX:
                formattedValue = AssetBalance.formatPrice(curAccount.Profit);
                break;
            case AssetBalanceItemColumnsEnum.PROJECTED_BALANCE_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.ProjectedBalance, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.INITIAL_MARGIN_REQ_PERCENT_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.MarginUsedPercent, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.MAINTENANCE_MARGIN_REQ_PERCENT_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.RiskLevelPercent, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.MARGIN_WARN_PERCENT_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.MarginWarningLevel, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.STOP_TRADING_LEVEL_PERCENT_COL_INDEX:
                formattedValue = MathUtils.TruncateDouble(curAccount.MarginLevel, 2) + ' %';
                break;
            case AssetBalanceItemColumnsEnum.AVAILABLE_FUNDS_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.AvailableFunds, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.MARGIN_WARNING_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.WarningMargin, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.TOTAL_MAINT_REQ_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.TotalMaintReq, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.WITHDRAWAL_AVAILABLE_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.WithdrawalAvailable, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.TODAY_GROSS_PNL_COL_INDEX:
            case AssetBalanceItemColumnsEnum.TODAY_FEES_COL_INDEX:
            case AssetBalanceItemColumnsEnum.TODAY_REALIZED_PNL_COL_INDEX:
                formattedValue = AssetBalance.formatPriceExactly(value);
                break;
            case AssetBalanceItemColumnsEnum.TODAY_VOLUME_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.TodayVolume, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.TODAY_TRADES_COUNT_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.TodayTrades, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.STOCKS_VALUE_COL_INDEX:
                formattedValue = AssetBalance.formatPriceExactly(value);
                break;
            case AssetBalanceItemColumnsEnum.CASH_BALANCE_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.CashBalance, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.INVESTED_FUND_CAPITAL_COL_INDEX:
                formattedValue = AssetBalance.formatPriceExactly(value);
                break;
            case AssetBalanceItemColumnsEnum.CURRENT_FUND_CAPITAL_COL_INDEX:
                formattedValue = AssetBalance.formatPriceExactly(value);
                break;
            case AssetBalanceItemColumnsEnum.FUND_CAPITAL_GAIN_COL_INDEX:
                formattedValue = AssetBalance.formatPriceExactly(0);
                break;
            case AssetBalanceItemColumnsEnum.INITIAL_MARGIN_REQ_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.MarginUsed, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.BALANCE_PLUS_ALL_RISKS_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.BalancePlusAllRisks, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.TRADING_STATUS_COL_INDEX:
                formattedValue = value;
                break;
            case AssetBalanceItemColumnsEnum.DAILY_LOSS_LIMIT_COL_INDEX:
                if (value === -1) {
                    formattedValue = '';
                }
                else {
                    formattedValue = this.AssetBalance.formatPrice(value);
                }
                break;
            case AssetBalanceItemColumnsEnum.MAX_ORDERS_PER_DAY_COL_INDEX:
                formattedValue = value > 0 ? value : '';
                break;
            case AssetBalanceItemColumnsEnum.MAX_POSITIONS_COUNT_COL_INDEX:
            case AssetBalanceItemColumnsEnum.MAX_PENDING_ORDERS_COUNT_COL_INDEX:
                if (value > 0) {
                    formattedValue = value;
                }
                break;
            case AssetBalanceItemColumnsEnum.MAX_ORDER_CAPITAL_COL_INDEX:
                if (value <= 0) {
                    formattedValue = '';
                    // или по умолч.
                    // else if (value == 0)  // commented out:#115409
                    //     formattedValue = Resources.getResource("panel.accounts.accountDetails.RiskManagement.MaxOrderCapital.Unlimited"); //FXCM
                }
                else {
                    formattedValue = AssetBalance.formatPrice(value);
                }
                break;
            case AssetBalanceItemColumnsEnum.MARGIN_AVAILABLE_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.MarginAvailable, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.CREDIT_VALUE_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.CreditValue, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.ALLOW_OVERNIGHT_TRADING_COL_INDEX:
                formattedValue = Resources.getResource(value
                    ? 'panel.accounts.accountDetails.RiskManagement.Trading.State.Enabled'
                    : 'panel.accounts.accountDetails.RiskManagement.Trading.State.Disabled');
                break;
            case AssetBalanceItemColumnsEnum.ACCOUNT_MODE_COL_INDEX: {
                const modePostfix = AccountUtils.GetAccountModeText(curAccount);
                formattedValue = Resources.getResource('panel.accounts.accountDetails.DemoOrLive.' + modePostfix);
                break;
            }
            case AssetBalanceItemColumnsEnum.TODAY_TURNOVER_COL_INDEX:
                if (curAccount.AccountType !== AccountType.MultiAsset) {
                    formattedValue = '';
                }
                else {
                    formattedValue = AssetBalance.formatPrice(AssetBalance.TodayTurnover);
                }
                break;
            case AssetBalanceItemColumnsEnum.ASSET_TYPE_COL_INDEX:
                formattedValue = Resources.getResource('enum.AssetType.' + AssetType[AssetBalance.Asset.Type]);
                break;
            case AssetBalanceItemColumnsEnum.INTEREST_RATE_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.InterestRatePercent, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.NEGATIVE_INTEREST_RATE_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.NegativeInterestRatePercent, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.TODAY_REBATES_COL_INDEX:
                formattedValue = AssetBalance.formatPriceExactly(value);
                break;
            case AssetBalanceItemColumnsEnum.BLOCKED_FOR_STOCKS_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.BlockedForStocks, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.TODAY_SWAPS_COL_INDEX:
                formattedValue = AssetBalance.formatPriceExactly(AssetBalance.TodaySwaps);
                break;
            case AssetBalanceItemColumnsEnum.UNSETTLED_CASH_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.UnsettledCash, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.TRAILING_DRAWDOWN_LEVEL_COL_INDEX:
                if (curAccount.TrailingDrawdownLevel === -1) {
                    formattedValue = '';
                }
                else {
                    formattedValue = this.AssetBalance.formatPrice(curAccount.TrailingDrawdownLevel);
                }
                break;
            case AssetBalanceItemColumnsEnum.TOTAL_MAX_POSITIONS_COUNT_COL_INDEX:
                if (curAccount.TotalMaxPositionsQty === -1) {
                    formattedValue = '';
                }
                else {
                    formattedValue = curAccount.TotalMaxPositionsQty.toString();
                }
                break;
            case AssetBalanceItemColumnsEnum.MAX_WEEKLY_LOSS_LIMIT_COL_INDEX:
                if (curAccount.MaxWeeklyLossLimit === -1) {
                    formattedValue = '';
                }
                else {
                    formattedValue = this.AssetBalance.formatPrice(curAccount.MaxWeeklyLossLimit);
                }
                break;
            case AssetBalanceItemColumnsEnum.CURRENT_DAILY_LOSS_COL_INDEX:
                if (curAccount.MaxDailyLossLimit === -1) {
                    formattedValue = '';
                }
                else {
                    formattedValue = Account.GetAccountFeatureString(value, AccountFeature.CurrentDailyLoss, curAccount, AssetBalance);
                }
                break;
            case AssetBalanceItemColumnsEnum.CURRENT_WEEKLY_LOSS_COL_INDEX:
                if (curAccount.MaxWeeklyLossLimit === -1) {
                    formattedValue = '';
                }
                else {
                    formattedValue = this.AssetBalance.formatPrice(curAccount.CurrentWeeklyLoss);
                }
                break;
            case AssetBalanceItemColumnsEnum.STOCKS_ORDERS_REQ_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.StocksOrdersReq, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.OPTION_VALUE_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.OptionValue, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.STOCKS_LIQUIDITY_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.StocksLiquidity, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.UNREALIZED_PL_COL_INDEX:
                if (curAccount.MaxUrealizedLossLimit === -1) {
                    formattedValue = '';
                }
                else {
                    formattedValue = this.AssetBalance.formatPrice(curAccount.ProfitBase > 0 ? 0 : -curAccount.ProfitBase);
                }
                break;
            case AssetBalanceItemColumnsEnum.UNREALIZED_LOSS_LIMIT_COL_INDEX:
                if (curAccount.MaxUrealizedLossLimit === -1) {
                    formattedValue = '';
                }
                else {
                    formattedValue = this.AssetBalance.formatPrice(curAccount.MaxUrealizedLossLimit);
                }
                break;
            case AssetBalanceItemColumnsEnum.DAILY_PROFIT_TARGET_COL_INDEX:
                if (value != null && value >= 0) {
                    formattedValue = AssetBalance.formatPrice(value);
                }
                else {
                    formattedValue = '';
                }
                break;
            case AssetBalanceItemColumnsEnum.TOTAL_VOLUME_LIMIT_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.VolumeLimitForAll, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.EQUITIES_VOLUME_LIMIT_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.VolumeLimitForEquities, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.FUTURES_VOLUME_LIMIT_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.VolumeLimitForFutures, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.OPTIONS_VOLUME_LIMIT_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.VolumeLimitForOptions, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.POWER_OF_ATTORNEY_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.PowerOfAttorney, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.MAX_ORDER_AMOUNT_COL_INDEX:
                formattedValue = curAccount.MaxOrderAmount === -1 ? '' : curAccount.MaxOrderAmount.toString();
                break;
            case AssetBalanceItemColumnsEnum.OPTION_PREMIUM_REQ_COL_INDEX:
                formattedValue = AssetBalance.formatPrice(AssetBalance.OptionPremiumReq);
                break;
            case AssetBalanceItemColumnsEnum.COLLATERAL_COL_INDEX:
                formattedValue = AssetBalance.formatPrice(AssetBalance.Collateral);
                break;
            case AssetBalanceItemColumnsEnum.WAIVED_MARGIN_AMOUNT_COL_INDEX:
                formattedValue =
                    value != null && value !== -1 ? AssetBalance.formatPrice(value) : StringUtils.NOT_AVAILABLE;
                break;
            case AssetBalanceItemColumnsEnum.WARNING_MARGIN_REQ_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.WarnMarginReq, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.WARNING_MARGIN_REQ_PERCENT_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.WarnMarginReqPercent, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.MARGIN_BEFORE_WARNING_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.MarginBeforeWarning, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.INITIAL_MARGIN_WITHOUT_WAIVED_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.InitialMarginWithoutWaived, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.BUYING_POWER_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.BuyingPower, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.DAY_TRADER_PATTERN_PROTECTION_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.DayTraderPatternProtection, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.AVAILABLE_DAY_TRADES_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.AvailableDayTrades, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.GIVEN_LEVERAGE_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.GivenLeverage, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.OPEN_BALANCE_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.OpenBalance, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.MAX_RELATIVE_DRAWDOWN_LEVEL_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.MaxRelativeDrawDownLevel, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.STOP_OUT_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.StopOut, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.LOSS_LIMIT_PER_TRADE_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.LossLimitPerTrade, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.INCOMING_FUNDS_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.IncomingFunds, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.EOD_TRAILING_DRAWDOWN_LEVEL_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.EODTrailingDrawdownLevel, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.MAX_RELATIVE_DAILY_LOSS_LIMIT_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.RelativeDailyLossLimit, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.LIVE_LEVERAGE_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.LiveLeverage, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.AVAILABLE_CASH_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.AvailableCash, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.TOTAL_POSITIONS_VALUE_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.TotalPositionsValue, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.FREE_COMMISSION_COL_INDEX:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.FreeCommission, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.BLOCKED_FOR_FIXED_INCOME:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.BlockedForFixedIncome, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.FIXED_INCOME_ORDERS_REQ:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.FixedIncomeOrdersReq, curAccount, AssetBalance);
                break;
            case AssetBalanceItemColumnsEnum.FIXED_INCOME_VALUE:
                formattedValue = Account.GetAccountFeatureString(value, AccountFeature.FixedIncomeValue, curAccount, AssetBalance);
                break;
            default:
                formattedValue = value === null || value === undefined ? '' : value.toString();
        }
        return new ColumnData(value, formattedValue);
    }
    getColumnValue(column) {
        const curAccount = this.Account;
        const AssetBalance = this.AssetBalance;
        const UseAccountCurrency = true;
        const UseAccCurrency = true; // fApp.MultiDataCache.UseAccCurrency;
        switch (column) {
            case AssetBalanceItemColumnsEnum.STATUS_COL_INDEX:
                return /* (Account.User.Status == 0) ? "Offline" : */ 'Online';
            case AssetBalanceItemColumnsEnum.ACCOUNT_COL_INDEX:
                return curAccount.FullAccString;
            case AssetBalanceItemColumnsEnum.BALANCE_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.Balance, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.BLOCKED_BALANCE_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.BlockedBalance, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.PROFIT_NET_COL_INDEX:
                return UseAccountCurrency
                    ? curAccount.ProfitNet
                        ? curAccount.ProfitNet
                        : 0
                    : curAccount.ProfitNetBase
                        ? curAccount.ProfitNetBase
                        : 0;
            case AssetBalanceItemColumnsEnum.PROFIT_COL_INDEX:
                return UseAccountCurrency ? curAccount.Profit : curAccount.ProfitBase;
            case AssetBalanceItemColumnsEnum.PROJECTED_BALANCE_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.ProjectedBalance, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.LOCKED_STATUS_COL_INDEX:
                if (curAccount.isMasterAccount) {
                    // return PammBase.GetNameElementByValue(BackOfficeObjectType.PammFundType, curAccount.FundType.toString()); - there is no such functionality
                    return null;
                }
                else {
                    return ' - ';
                }
            case AssetBalanceItemColumnsEnum.TERMINAL_TYPE_COL_INDEX:
                // TODO
                return '';
            // if (Account.User.Status == 0) //если offline - ничего не показываем
            //     return "";
            // else
            //     return AssetBalanceItemColumnsEnum.GetClientTypeStr(Account.User.ClientType);
            case AssetBalanceItemColumnsEnum.ORDERS_COUNT_COL_INDEX:
                return curAccount.AccountType === AccountType.MultiAsset
                    ? AssetBalance.OpenOrdNumber
                    : curAccount.OpenOrdNumber;
            case AssetBalanceItemColumnsEnum.POSITIONS_COUNT_COL_INDEX:
                return curAccount.AccountType === AccountType.MultiAsset
                    ? AssetBalance.OpenPosNumber
                    : curAccount.OpenPosNumber;
            case AssetBalanceItemColumnsEnum.INITIAL_MARGIN_REQ_PERCENT_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.MarginUsedPercent, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.MAINTENANCE_MARGIN_REQ_PERCENT_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.RiskLevelPercent, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.MARGIN_WARN_PERCENT_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.MarginWarningLevel, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.STOP_TRADING_LEVEL_PERCENT_COL_INDEX:
                return curAccount.MarginLevel;
            case AssetBalanceItemColumnsEnum.AVAILABLE_FUNDS_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.AvailableFunds, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.MARGIN_WARNING_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.WarningMargin, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.TOTAL_MAINT_REQ_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.TotalMaintReq, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.WITHDRAWAL_AVAILABLE_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.WithdrawalAvailable, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.USER_ID_COL_INDEX:
                return curAccount.userID;
            case AssetBalanceItemColumnsEnum.USER_LOGIN_COL_INDEX:
                return curAccount.userLogin;
            case AssetBalanceItemColumnsEnum.OWNER_COL_INDEX:
                return curAccount.Owner;
            case AssetBalanceItemColumnsEnum.EMAIL_COL_INDEX:
                return curAccount.Email;
            case AssetBalanceItemColumnsEnum.TODAY_GROSS_PNL_COL_INDEX:
                return UseAccountCurrency ? AssetBalance.TodayGrossPNL : AssetBalance.TodayGrossPNLCrossPrice;
            case AssetBalanceItemColumnsEnum.TODAY_FEES_COL_INDEX:
                return UseAccountCurrency ? AssetBalance.TodayFees : AssetBalance.TodayFeesCrossPrice;
            case AssetBalanceItemColumnsEnum.TODAY_REALIZED_PNL_COL_INDEX:
                return this.UseAccountCurrency()
                    ? AssetBalance.TodayRealizedPNL
                    : AssetBalance.TodayRealizedPNLCrossPrice;
            case AssetBalanceItemColumnsEnum.TODAY_VOLUME_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.TodayVolume, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.TODAY_TRADES_COUNT_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.TodayTrades, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.STOCKS_VALUE_COL_INDEX:
                return UseAccCurrency ? curAccount.StockValue : curAccount.StockValueCrossPrice;
            case AssetBalanceItemColumnsEnum.CASH_BALANCE_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.CashBalance, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.INVESTED_FUND_CAPITAL_COL_INDEX:
                return UseAccountCurrency
                    ? /* AssetBalance.BlockedForPAMM + */ curAccount.StartPammCapital
                    : /* AssetBalance.BlockedForPammCrossPrice + */ curAccount.StartPammCapitalCrossPrice;
            case AssetBalanceItemColumnsEnum.USER_GROUP_COL_INDEX:
                if (this.userGroupName != null) {
                    return this.userGroupName;
                }
                else {
                    return '';
                }
            case AssetBalanceItemColumnsEnum.CURRENT_FUND_CAPITAL_COL_INDEX:
                return UseAccountCurrency
                    ? curAccount.CurrPammCapital /* + Account.StartPammCapital */
                    : curAccount.CurrPammCapitalCrossPrice /* + Account.StartPammCapitalCrossPrice */;
            case AssetBalanceItemColumnsEnum.FUND_CAPITAL_GAIN_COL_INDEX: {
                return 0.0;
            }
            case AssetBalanceItemColumnsEnum.TELEPHONE_NUMBER_COL_INDEX:
                return !curAccount.TelephoneNumber
                    ? Resources.getResource('panel.accounts.N/A_TelephoneNumber')
                    : curAccount.TelephoneNumber;
            case AssetBalanceItemColumnsEnum.INITIAL_MARGIN_REQ_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.MarginUsed, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.BALANCE_PLUS_ALL_RISKS_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.BalancePlusAllRisks, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.TRADING_STATUS_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.CurrentTradingStatus, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.DAILY_LOSS_LIMIT_COL_INDEX:
                return curAccount.MaxDailyLossLimit;
            case AssetBalanceItemColumnsEnum.MAX_ORDERS_PER_DAY_COL_INDEX:
                return DataCache.getRuleNumberValueForAccount(RulesSet.VALUE_MAX_DAY_ORDERS, curAccount, -1); // hsa: FIELD_MAX_ORDERS_PER_DAY?
            case AssetBalanceItemColumnsEnum.MAX_POSITIONS_COUNT_COL_INDEX:
                return curAccount.MaxPositions >= 0 ? curAccount.MaxPositions : 0;
            case AssetBalanceItemColumnsEnum.MAX_PENDING_ORDERS_COUNT_COL_INDEX:
                return curAccount.MaxPendingOrders >= 0 ? curAccount.MaxPendingOrders : 0;
            case AssetBalanceItemColumnsEnum.MAX_ORDER_CAPITAL_COL_INDEX:
                return curAccount.MaxOrderCapital !== -1 ? curAccount.MaxOrderCapital : 0;
            case AssetBalanceItemColumnsEnum.MARGIN_AVAILABLE_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.MarginAvailable, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.CREDIT_VALUE_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.CreditValue, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.ALLOW_OVERNIGHT_TRADING_COL_INDEX:
                return curAccount.AllowOvernightTrading;
            case AssetBalanceItemColumnsEnum.ACCOUNT_MODE_COL_INDEX:
                return curAccount.AccountMode;
            case AssetBalanceItemColumnsEnum.ASSET_NAME_COL_INDEX:
                return AssetBalance.Asset.Name;
            case AssetBalanceItemColumnsEnum.ASSET_DESCRIPTION_COL_INDEX:
                return AssetBalance.Asset.Description;
            case AssetBalanceItemColumnsEnum.ACCOUNT_TYPE_COL_INDEX:
                return Resources.getResource('enum.AccountType.' + getAccountTypeStr(curAccount.AccountType));
            case AssetBalanceItemColumnsEnum.TODAY_TURNOVER_COL_INDEX:
                return AssetBalance.TodayTurnover;
            case AssetBalanceItemColumnsEnum.ASSET_TYPE_COL_INDEX:
                return AssetBalance.Asset.Type;
            case AssetBalanceItemColumnsEnum.INTEREST_RATE_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.InterestRatePercent, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.NEGATIVE_INTEREST_RATE_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.NegativeInterestRatePercent, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.TODAY_REBATES_COL_INDEX:
                return UseAccountCurrency ? AssetBalance.TodayRebates : AssetBalance.TodayRebatesCrossPrice;
            case AssetBalanceItemColumnsEnum.BLOCKED_FOR_STOCKS_DEPRECATED_COL_INDEX: // #38604
                return curAccount.BlockedForStocksAccountCurrency / curAccount.getCrossPrice(); // в валюте сервера
            case AssetBalanceItemColumnsEnum.BLOCKED_FOR_STOCKS_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.BlockedForStocks, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.TODAY_SWAPS_COL_INDEX:
                return UseAccCurrency ? AssetBalance.TodaySwaps : AssetBalance.TodaySwapsCrossPrice;
            case AssetBalanceItemColumnsEnum.UNSETTLED_CASH_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.UnsettledCash, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.TRAILING_DRAWDOWN_LEVEL_COL_INDEX:
                if (curAccount.TrailingDrawdownLevel === -1) {
                    return -1;
                }
                return curAccount.TrailingDrawdownLevel;
            case AssetBalanceItemColumnsEnum.TOTAL_MAX_POSITIONS_COUNT_COL_INDEX:
                return curAccount.TotalMaxPositionsQty;
            case AssetBalanceItemColumnsEnum.MAX_WEEKLY_LOSS_LIMIT_COL_INDEX:
                if (curAccount.MaxWeeklyLossLimit === -1) {
                    return 0;
                }
                return curAccount.MaxWeeklyLossLimit;
            case AssetBalanceItemColumnsEnum.CURRENT_DAILY_LOSS_COL_INDEX:
                if (curAccount.MaxDailyLossLimit === -1) {
                    return 0;
                }
                return Account.GetAccountFeature(AccountFeature.CurrentDailyLoss, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.CURRENT_WEEKLY_LOSS_COL_INDEX:
                if (curAccount.MaxWeeklyLossLimit === -1) {
                    return 0;
                }
                return curAccount.CurrentWeeklyLoss;
            case AssetBalanceItemColumnsEnum.STOCKS_ORDERS_REQ_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.StocksOrdersReq, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.OPTION_VALUE_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.OptionValue, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.STOCKS_LIQUIDITY_COL_INDEX: // #62325
                return Account.GetAccountFeature(AccountFeature.StocksLiquidity, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.UNREALIZED_PL_COL_INDEX:
                if (curAccount.MaxUrealizedLossLimit === -1) {
                    return 0;
                }
                return curAccount.ProfitBase > 0 ? 0 : -curAccount.ProfitBase;
            case AssetBalanceItemColumnsEnum.UNREALIZED_LOSS_LIMIT_COL_INDEX:
                if (curAccount.MaxUrealizedLossLimit === -1) {
                    return 0;
                }
                return curAccount.MaxUrealizedLossLimit;
            case AssetBalanceItemColumnsEnum.DAILY_PROFIT_TARGET_COL_INDEX:
                return 'TODO'; // RulesSet.GetRuleValue(RulesSet.VALUE_DAILY_PROFIT_TARGET, Account);
            case AssetBalanceItemColumnsEnum.TOTAL_VOLUME_LIMIT_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.VolumeLimitForAll, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.EQUITIES_VOLUME_LIMIT_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.VolumeLimitForEquities, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.FUTURES_VOLUME_LIMIT_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.VolumeLimitForFutures, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.OPTIONS_VOLUME_LIMIT_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.VolumeLimitForOptions, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.POWER_OF_ATTORNEY_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.PowerOfAttorney, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.MAX_ORDER_AMOUNT_COL_INDEX:
                return curAccount.MaxOrderAmount;
            case AssetBalanceItemColumnsEnum.OPTION_PREMIUM_REQ_COL_INDEX:
                return AssetBalance.OptionPremiumReq;
            case AssetBalanceItemColumnsEnum.COLLATERAL_COL_INDEX:
                return AssetBalance.Collateral;
            case AssetBalanceItemColumnsEnum.WAIVED_MARGIN_AMOUNT_COL_INDEX:
                return curAccount.WaivedMargin;
            case AssetBalanceItemColumnsEnum.WARNING_MARGIN_REQ_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.WarnMarginReq, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.WARNING_MARGIN_REQ_PERCENT_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.WarnMarginReqPercent, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.MARGIN_BEFORE_WARNING_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.MarginBeforeWarning, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.INITIAL_MARGIN_WITHOUT_WAIVED_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.InitialMarginWithoutWaived, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.BUYING_POWER_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.BuyingPower, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.DAY_TRADER_PATTERN_PROTECTION_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.DayTraderPatternProtection, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.AVAILABLE_DAY_TRADES_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.AvailableDayTrades, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.GIVEN_LEVERAGE_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.GivenLeverage, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.OPEN_BALANCE_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.OpenBalance, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.MAX_RELATIVE_DRAWDOWN_LEVEL_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.MaxRelativeDrawDownLevel, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.STOP_OUT_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.StopOut, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.LOSS_LIMIT_PER_TRADE_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.LossLimitPerTrade, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.INCOMING_FUNDS_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.IncomingFunds, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.EOD_TRAILING_DRAWDOWN_LEVEL_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.EODTrailingDrawdownLevel, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.MAX_RELATIVE_DAILY_LOSS_LIMIT_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.RelativeDailyLossLimit, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.LIVE_LEVERAGE_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.LiveLeverage, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.AVAILABLE_CASH_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.AvailableCash, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.TOTAL_POSITIONS_VALUE_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.TotalPositionsValue, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.FREE_COMMISSION_COL_INDEX:
                return Account.GetAccountFeature(AccountFeature.FreeCommission, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.BLOCKED_FOR_FIXED_INCOME:
                return Account.GetAccountFeature(AccountFeature.BlockedForFixedIncome, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.FIXED_INCOME_ORDERS_REQ:
                return Account.GetAccountFeature(AccountFeature.FixedIncomeOrdersReq, curAccount, AssetBalance);
            case AssetBalanceItemColumnsEnum.FIXED_INCOME_VALUE:
                return Account.GetAccountFeature(AccountFeature.FixedIncomeValue, curAccount, AssetBalance);
            default:
                return null;
        }
    }
    GetColumnParams(i) {
        const result = AssetBalanceItem.columnsParams[i];
        result.Hidden = result.DefaultHidden;
        result.MenuIndex = this.GetMenuIndex(result.HeaderKey);
        const dc = DataCache;
        // let sess = SessionSettings
        if (!dc) {
            return result;
        }
        // TODO. User obj. Refactor.
        // let checkRuleUser = sess.LoginUser || dc.LoginUser
        // TODO.
        // #22483 Admin и видимость полей
        // TODO. UserAdminRule.
        const isUSERSADMIN = false;
        // bool isUSERSADMIN = dc.isAllowedForUser(RulesSet.FUNCTION_USERSADMIN, checkRuleUser)
        if (!isUSERSADMIN &&
            (i === AssetBalanceItemColumnsEnum.STATUS_COL_INDEX ||
                i === AssetBalanceItemColumnsEnum.LOCKED_STATUS_COL_INDEX ||
                i === AssetBalanceItemColumnsEnum.FUND_TYPE_COL_INDEX ||
                i === AssetBalanceItemColumnsEnum.TERMINAL_TYPE_COL_INDEX ||
                i === AssetBalanceItemColumnsEnum.USER_GROUP_COL_INDEX)) {
            result.Hidden = true;
            return result;
        }
        const Account = this.Account;
        if (Account && dc) {
            if ((i === AssetBalanceItemColumnsEnum.STATUS_COL_INDEX ||
                i === AssetBalanceItemColumnsEnum.LOCKED_STATUS_COL_INDEX ||
                i === AssetBalanceItemColumnsEnum.FUND_TYPE_COL_INDEX ||
                i === AssetBalanceItemColumnsEnum.TERMINAL_TYPE_COL_INDEX) &&
                !isUSERSADMIN) {
                result.Hidden = true;
            }
            else if (i === AssetBalanceItemColumnsEnum.LOCKED_STATUS_COL_INDEX) {
                result.Hidden = true || Account.isMasterAccount;
            }
            else {
                result.Hidden = false;
            }
            if (i === AssetBalanceItemColumnsEnum.TODAY_TURNOVER_COL_INDEX) {
                // #33209
                result.Hidden = Account.AccountType !== AccountType.MultiAsset;
            }
            if (i === AssetBalanceItemColumnsEnum.DAY_TRADER_PATTERN_PROTECTION_COL_INDEX ||
                i === AssetBalanceItemColumnsEnum.AVAILABLE_DAY_TRADES_COL_INDEX) {
                result.Hidden = !Account.DayTraderPattern;
            }
        }
        else {
            if (i === AssetBalanceItemColumnsEnum.STATUS_COL_INDEX && !isUSERSADMIN) {
                result.Hidden = true;
            }
            else {
                result.Hidden = i === AssetBalanceItemColumnsEnum.LOCKED_STATUS_COL_INDEX;
            }
            if (i === AssetBalanceItemColumnsEnum.TODAY_TURNOVER_COL_INDEX) {
                // #33209, #33495
                let hasMultiAcc = false;
                const accDict = dc.getAccounts();
                for (const accId in accDict) {
                    const acc = accDict[accId];
                    if (acc.AccountType === AccountType.MultiAsset) {
                        hasMultiAcc = true;
                        break;
                    }
                }
                result.Hidden = !hasMultiAcc;
            }
        }
        const a = ACCOUNT_COLUMN_RULE.get(i);
        const columnAvailable = isNullOrUndefined(a) || DataCache.IsColumnAvailable(a.group, a.rule);
        if (!columnAvailable) {
            result.Hidden = true;
        }
        // ну, и в любом случае если ключ колонки захайден
        if (Resources.isHidden(result.HeaderKey)) {
            result.Hidden = true;
        }
        if (i === AssetBalanceItemColumnsEnum.ESTIMATED_VALUE_COL_INDEX ||
            i === AssetBalanceItemColumnsEnum.ORDERS_COUNT_COL_INDEX ||
            i === AssetBalanceItemColumnsEnum.POSITIONS_COUNT_COL_INDEX ||
            i === AssetBalanceItemColumnsEnum.PROJECTED_BALANCE_COL_INDEX) {
            result.TextCompareColumn = true;
        }
        if (AssetBalanceItem.HiddenTable[i]) {
            result.Hidden = true;
        }
        // все колонки кроме этих нужно скрыть если приходит рул
        if (i !== AssetBalanceItemColumnsEnum.TODAY_REALIZED_PNL_COL_INDEX &&
            i !== AssetBalanceItemColumnsEnum.OWNER_COL_INDEX &&
            i !== AssetBalanceItemColumnsEnum.USER_LOGIN_COL_INDEX &&
            i !== AssetBalanceItemColumnsEnum.ACCOUNT_MODE_COL_INDEX) {
            if (DataCache.isAllowedForMyUser(RulesSet.FUNCTION_EXT_RISK)) {
                result.Hidden = true;
            }
        }
        // Free column
        if (i >= 119 && i <= 130) {
            result.Hidden = true;
        }
        // Free column
        if (i === 141) {
            result.Hidden = true;
        }
        return result;
    }
    fireUpdate() { }
    GetDataType(columnIndex) {
        if (columnIndex < AssetBalanceItem.dataTypes.length) {
            return AssetBalanceItem.dataTypes[columnIndex];
        }
        else {
            return QuickTableComparingType.Double;
        }
    }
    NeedSortMenu() {
        return true;
    }
    GetMenuIndex(headerKey) {
        if (AssetBalanceItem.ListMenuIndex[headerKey]) {
            return AssetBalanceItem.ListMenuIndex[headerKey];
        }
        else {
            return 10000;
        }
    }
}
AssetBalanceItem.dataTypes = [
    QuickTableComparingType.String, // 0
    QuickTableComparingType.String,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.String,
    QuickTableComparingType.String,
    QuickTableComparingType.String,
    QuickTableComparingType.Int, // 10
    QuickTableComparingType.Int,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double, // 20
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.String,
    QuickTableComparingType.String,
    QuickTableComparingType.String,
    QuickTableComparingType.String,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double, // 30
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Int,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.String,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double, // 40
    QuickTableComparingType.String,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double, // 45
    QuickTableComparingType.Double,
    QuickTableComparingType.String,
    QuickTableComparingType.String,
    QuickTableComparingType.String,
    QuickTableComparingType.Double, // 50
    QuickTableComparingType.Double,
    QuickTableComparingType.Int,
    QuickTableComparingType.Int,
    QuickTableComparingType.Int,
    QuickTableComparingType.Double, // 55
    QuickTableComparingType.Int,
    QuickTableComparingType.Int,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double, // 60
    QuickTableComparingType.Double,
    QuickTableComparingType.String, // 62
    QuickTableComparingType.Int,
    QuickTableComparingType.String,
    QuickTableComparingType.String,
    QuickTableComparingType.Double,
    QuickTableComparingType.String,
    QuickTableComparingType.Double,
    QuickTableComparingType.Int,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double, // 71
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    // #43336
    QuickTableComparingType.String, // 75
    QuickTableComparingType.String, // 76
    QuickTableComparingType.String, // 77
    QuickTableComparingType.String, // 78
    QuickTableComparingType.String, // 79
    QuickTableComparingType.String, // 80
    QuickTableComparingType.String,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double, // 85
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double, // 90
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double, // 95
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.String, // 100
    QuickTableComparingType.Int,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double
];
// Здесь хранятся настройки колонок (один экземпляр на класс)
AssetBalanceItem.columnsParams = [
    /* 0 */ new ColumnParams('panel.accounts.Status', 60, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.accounts.Login', 100, QTAlignment.Left, TableTotalsVisibility.All, true, true),
    new ColumnParams('panel.accounts.Balance', 60, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.accounts.LockedBalance', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.ProfitNet', 75, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    /* 5 */ new ColumnParams('panel.accounts.Profit', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.CurBalance', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.LockedBy', 60, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.accounts.Role', 60, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.accounts.ClientType', 60, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    /* 10 */ new ColumnParams('panel.accounts.Orders', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.Positions', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.MinMargin', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.BlockedByOrders', 75, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.CurMargin', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 15 */ new ColumnParams('panel.accounts.MaintMargin', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.Leverage', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.marginWarnPercent', 75, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.stopTradingPercent', 75, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.stopOutPercent', 75, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 20 */ new ColumnParams('panel.accounts.AvailableMargin', 60, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.accounts.MarginWarning', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.MarginStopTrade', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.MarginStopOut', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.WithdrawalAvailable', 60, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    /* 25 */ new ColumnParams('panel.accounts.UserID', 75, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.accounts.UserLogin', 75, QTAlignment.Left, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.accounts.Owner', 75, QTAlignment.Left, TableTotalsVisibility.None, false, true, false, true),
    new ColumnParams('panel.accounts.Email', 75, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.accounts.GrossPNL', 75, QTAlignment.Right, TableTotalsVisibility.All, false, false, false, true),
    /* 30 */ new ColumnParams('panel.accounts.TodayFees', 75, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.BeginBalance', 75, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.TodayVolume', 75, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.accounts.TodayTrades', 75, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.accounts.StocksValue', 75, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    /* 35 */ new ColumnParams('panel.accounts.AccountValue', 75, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.CashBalance', 75, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.BlockedByPamm', 75, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.UserGroup', 70, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.accounts.CurrentFundCapital', 75, QTAlignment.Right, TableTotalsVisibility.All, false, false, false, true),
    /* 40 */ new ColumnParams('panel.accounts.FundCapitalGain', 75, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.TelephoneNumber', 75, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.accounts.MarginUsedNew', 60, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.accounts.BalancePlusAllRisks', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.MarginRequirement', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    /* 45 */ new ColumnParams('panel.accounts.MarginDeficiency', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.MarginSurplus', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    /* 47 */ new ColumnParams('panel.accounts.IntradayRiskManagement', 60, QTAlignment.Left, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.CurrentStatus', 60, QTAlignment.Left, TableTotalsVisibility.None, false, false, false, true),
    /* 49 */ new ColumnParams('panel.accounts.AllowTradingPrePost', 60, QTAlignment.Left, TableTotalsVisibility.All, false, false),
    // в этом блоке value возвращает -1, если считать тоталы, надо учесть
    /* 50 */ new ColumnParams('panel.accounts.MaxDailyLossLimit', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false, false, false, -1.0),
    new ColumnParams('panel.accounts.MaxDayVolume', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false, false, false, -1.0),
    /* 52 */ new ColumnParams('panel.accounts.MaxOrdersPerDay', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.MaxPositions', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.MaxPendingOrders', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 55 */ new ColumnParams('panel.accounts.MaxOrderCapital', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false, false, false, -1.0),
    new ColumnParams('panel.accounts.MaximumLot', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.MaxPositionQtyPerSymbol', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.MarginAvailableReal', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.FundsUsed', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false, false, true),
    /* 60 */ new ColumnParams('panel.accounts.FundsUsedPercent', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.CreditValue', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.AllowOvernightTrading', 60, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.AccountMode', 60, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.Asset', 60, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.accounts.AssetDescription', 60, QTAlignment.Left, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.Estimate value', 60, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    /* 67 */ new ColumnParams('panel.accounts.AccountType', 60, QTAlignment.Left, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.accounts.TodayTurnover', 60, QTAlignment.Left, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.AssetType', 60, QTAlignment.Left, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.InterestRate', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    /* 71 */ new ColumnParams('panel.accounts.TodayRebates', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.BlockedForStocks', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.MarginForOthers', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.BlockedForStocksNew', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.Commissions', 60, QTAlignment.Left, TableTotalsVisibility.All, false, true),
    new ColumnParams('panel.accounts.Swap', 60, QTAlignment.Left, TableTotalsVisibility.All, false, true),
    new ColumnParams('panel.accounts.Spreads', 60, QTAlignment.Left, TableTotalsVisibility.All, false, true),
    new ColumnParams('panel.accounts.Murkups', 60, QTAlignment.Left, TableTotalsVisibility.All, false, true),
    new ColumnParams('panel.accounts.MarketDataCharging', 60, QTAlignment.Left, TableTotalsVisibility.All, false, true),
    /* 80 */ new ColumnParams('panel.accounts.MarginAndRisks', 60, QTAlignment.Left, TableTotalsVisibility.All, false, true),
    new ColumnParams('panel.accounts.InsertOnBalance', 60, QTAlignment.Left, TableTotalsVisibility.All, false, true),
    // Колонка спциально заведена для запихивания всякого мусора по аккаунту
    new ColumnParams('panel.accounts.AccountDescription', 60, QTAlignment.Left, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.TodaySwaps', 60, QTAlignment.Left, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.UnsettledCash', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    /* 85 */ new ColumnParams('panel.accounts.MaxPendingOrdersValue', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.TrailingDrawdownLevel', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false, false, false, -1.0),
    new ColumnParams('panel.accounts.TotalMaxPositionsQty', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false, false, false, -1.0),
    new ColumnParams('panel.accounts.MaxWeekLostLimit', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false, false, false, -1.0),
    new ColumnParams('panel.accounts.CurrentDailyLoss', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    /* 90 */ new ColumnParams('panel.accounts.CurrentWeeklyLoss', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.StocksOrdersReq', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.OptionValue', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.StocksLiquidity', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.UnrealizedLoss', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    /* 95 */ new ColumnParams('panel.accounts.UnrealizedLossLimit', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false, false, false, -1.0),
    new ColumnParams('panel.accounts.MaxDailyProfit', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.VolumeLimitForEQUITIES', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.VolumeLimitForFUTURES', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.VolumeLimitForOPTIONS', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    /* 100 */ new ColumnParams('panel.accounts.PowerOfAttorney', 60, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.MaxOrderAmount', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false, false, false, -1),
    new ColumnParams('panel.accounts.OptionPremiumReq', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.Collateral', 60, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.accounts.WaivedMargin', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    /* 105 */ new ColumnParams('panel.accounts.WarningMarginReq', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.WarningMarginReqPercent', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.MarginBeforeWarning', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.InitialMarginWithoutWaived', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.BuyingPower', 60, QTAlignment.Left, TableTotalsVisibility.All, true, false),
    /* 110 */ new ColumnParams('panel.accounts.DayTraderPatternProtection', 60, QTAlignment.Left, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.accounts.AvailableDayTrades', 60, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.accounts.GivenLeverage', 60, QTAlignment.Left, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.accounts.OpenBalance', 60, QTAlignment.Left, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.accounts.MaxRelativeDrawDownLevel', 60, QTAlignment.Left, TableTotalsVisibility.All, true, false),
    /* 115 */ new ColumnParams('panel.accounts.StopOut', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.LossLimitPerTrade', 60, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.accounts.IncomingFunds', 60, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.accounts.VolumeLimitForALL', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false), //Free column
    /* 120 */ new ColumnParams('', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false), //Free column
    new ColumnParams('', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false), //Free column
    new ColumnParams('', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false), //Free column
    new ColumnParams('', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false), //Free column
    new ColumnParams('', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false), //Free column
    /* 125 */ new ColumnParams('', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false), //Free column
    new ColumnParams('', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false), //Free column
    new ColumnParams('', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false), //Free column
    new ColumnParams('', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false), //Free column
    new ColumnParams('', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false), //Free column
    /* 130 */ new ColumnParams('', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false), //Free column
    new ColumnParams('panel.accounts.EODTrailingDrawdownLevel', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.RelativeDailyLossLimit', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.LiveLeverage', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.accounts.AvailableCash', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 135 */ new ColumnParams('panel.accounts.TotalPositionsValue', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.FreeCommission', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.BlockedForFixedIncome', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.FixedIncomeOrdersReq', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.accounts.FixedIncomeValue', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 140 */ new ColumnParams('panel.accounts.NegativeInterestRate', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('', 60, QTAlignment.Right, TableTotalsVisibility.All, false, false) //Free column
];
AssetBalanceItem.ListMenuIndex = {
    // sorted
    'panel.accounts.Balance': 0,
    'panel.accounts.CurBalance': 10,
    'panel.accounts.BalancePlusAllRisks': 20,
    'panel.accounts.AccountValue': 30,
    'panel.accounts.CreditValue': 40,
    'panel.accounts.AvailableMargin': 50,
    'panel.accounts.stopOutPercent': 60,
    'panel.accounts.LockedBalance': 70,
    'panel.accounts.CashBalance': 80,
    'panel.accounts.UnsettledCash': 90,
    'panel.accounts.IncomingFunds': 91,
    'panel.accounts.WithdrawalAvailable': 100,
    'panel.accounts.InterestRate': 110,
    'panel.accounts.NegativeInterestRate': 111,
    'panel.accounts.Estimate value': 120,
    'panel.accounts.FundsUsed': 130,
    'panel.accounts.FundsUsedPercent': 140,
    'panel.accounts.MarginUsedNew': 150,
    'panel.accounts.CurMargin': 160,
    'panel.accounts.MaintMargin': 170,
    'panel.accounts.Leverage': 180,
    'panel.accounts.MarginAvailableReal': 190,
    'panel.accounts.marginWarnPercent': 200,
    'panel.accounts.MarginWarning': 210,
    'panel.accounts.MarginStopOut': 220,
    'panel.accounts.MarginRequirement': 230,
    'panel.accounts.MinMargin': 240,
    'panel.accounts.BlockedByOrders': 250,
    'panel.accounts.BlockedForStocksNew': 260,
    'panel.accounts.BlockedForStocks': 270,
    'panel.accounts.StocksValue': 280,
    'panel.accounts.OptionValue': 281,
    'panel.accounts.MarginForOthers': 290,
    'panel.accounts.StocksOrdersReq': 300,
    'panel.accounts.StocksLiquidity': 305,
    'panel.accounts.OptionPremiumReq': 306,
    'panel.accounts.WaivedMargin': 307,
    'panel.accounts.Collateral': 308,
    'panel.accounts.BuyingPower': 309,
    'panel.accounts.GivenLeverage': 310,
    'panel.accounts.CurrentFundCapital': 310,
    'panel.accounts.FundCapitalGain': 320,
    'panel.accounts.Profit': 330,
    'panel.accounts.ProfitNet': 340,
    'panel.accounts.Positions': 350,
    'panel.accounts.Orders': 360,
    'panel.accounts.StopOut': 361,
    // Unsettled cash -  not found
    'panel.accounts.BlockedByPamm': 370,
    'panel.accounts.GrossPNL': 380,
    'panel.accounts.BeginBalance': 390,
    'panel.accounts.TodayFees': 400,
    'panel.accounts.TodayVolume': 410,
    'panel.accounts.TodayTrades': 420,
    'panel.accounts.TodayTurnover': 430,
    'panel.accounts.TodayRebates': 440,
    'panel.accounts.TodaySwaps': 445,
    'panel.accounts.CurrentStatus': 450,
    'panel.accounts.AllowTradingPrePost': 460,
    'panel.accounts.MaxDailyLossLimit': 470,
    'panel.accounts.LossLimitPerTrade': 471,
    'panel.accounts.MaxWeekLostLimit': 480,
    'panel.accounts.TotalMaxPositionsQty': 490,
    'panel.accounts.TrailingDrawdownLevel': 500,
    'panel.accounts.CurrentDailyLoss': 510,
    'panel.accounts.CurrentWeeklyLoss': 520,
    'panel.accounts.EODTrailingDrawdownLevel': 530,
    // "panel.accounts.MaxDayVolume": 530,
    'panel.accounts.MaxRelativeDrawDownLevel': 535,
    'panel.accounts.MaxOrdersPerDay': 540,
    'panel.accounts.MaxPositions': 550,
    'panel.accounts.MaxPendingOrders': 560,
    'panel.accounts.MaxOrderAmount': 565,
    'panel.accounts.MaxOrderCapital': 570,
    'panel.accounts.MaximumLot': 580,
    'panel.accounts.MaxPositionQtyPerSymbol': 590,
    'panel.accounts.AllowOvernightTrading': 600,
    'panel.accounts.VolumeLimitForALL': 602,
    'panel.accounts.VolumeLimitForEQUITIES': 604,
    'panel.accounts.VolumeLimitForFUTURES': 605,
    'panel.accounts.VolumeLimitForOPTIONS': 606,
    'panel.accounts.Owner': 620,
    'panel.accounts.UserID': 625,
    'panel.accounts.UserLogin': 630,
    'panel.accounts.UserGroup': 640,
    'panel.accounts.Email': 650,
    'panel.accounts.Login': 660,
    'panel.accounts.AccountType': 670,
    'panel.accounts.LockedBy': 680,
    'panel.accounts.AccountMode': 690,
    'panel.accounts.ClientType': 700,
    'panel.accounts.Status': 710,
    'panel.accounts.TelephoneNumber': 720,
    'panel.accounts.Asset': 730,
    'panel.accounts.AssetDescription': 740,
    'panel.accounts.AssetType': 750,
    // end sorted
    'panel.accounts.Role': 760,
    /* hidden */ 'panel.accounts.stopTradingPercent': 770,
    /* hidden */ 'panel.accounts.MarginStopTrade': 780,
    /* 45 */ 'panel.accounts.MarginDeficiency': 790,
    'panel.accounts.MarginSurplus': 800,
    'panel.accounts.IntradayRiskManagement': 810,
    'panel.accounts.WarningMarginReq': 820,
    'panel.accounts.WarningMarginReqPercent': 825,
    'panel.accounts.MarginBeforeWarning': 830,
    'panel.accounts.InitialMarginWithoutWaived': 835,
    'panel.accounts.LiveLeverage': 840
};
AssetBalanceItem.HiddenTable = {
    // [AssetBalanceItemColumnsEnum.STATUS_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.LOCKED_STATUS_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.INITIAL_MARGIN_REQ_FOR_POSITIONS_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.INITIAL_MARGIN_REQ_FOR_ORDERS_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.LEVERAGE_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.STOP_TRADING_LEVEL_PERCENT_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.MARGIN_STOP_TRADE_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.ACCOUNT_VALUE_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.INVESTED_FUND_CAPITAL_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.CURRENT_FUND_CAPITAL_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.FUND_CAPITAL_GAIN_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.MARGIN_REQUIREMENT_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.MARGIN_DEFICIENCY_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.MARGIN_SURPLUS_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.ALLOW_TRADING_PRE_POST_COL_INDEX]: true,
    // [AssetBalanceItemColumnsEnum.MAX_ORDERS_PER_DAY_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.MAXIMUM_LOT_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.MAX_POSITION_QTY_PER_SYMBOL_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.FUNDS_USED_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.FUNDS_USED_PERCENT_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.ESTIMATED_VALUE_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.COMMISSIONS_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.SWAP_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.SPREADS_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.MARKUPS_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.MARKET_DATA_CHARGING_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.MARGIN_AND_RISKS_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.INSERT_ON_BALANCE_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.MAX_PENDING_ORDERS_VALUE_COL_INDEX]: true,
    // InfoFields
    [AssetBalanceItemColumnsEnum.ASSET_TYPE_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.ASSET_DESCRIPTION_COL_INDEX]: true,
    // [AssetBalanceItemColumnsEnum.ASSET_NAME_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.TELEPHONE_NUMBER_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.ACCOUNT_MODE_COL_INDEX]: true,
    // [AssetBalanceItemColumnsEnum.ACCOUNT_TYPE_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.EMAIL_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.USER_ID_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.USER_LOGIN_COL_INDEX]: true,
    [AssetBalanceItemColumnsEnum.OWNER_COL_INDEX]: true
};
